// Shared helpers for the Book desk module faces (BookModulesRow1-3.tsx).
//
// Every query uses the SAME key and options as the panel that owns it, so a
// face and its drawer share one cache entry. A dormant book (`bookStatus ===
// null`, the live book today) 404s on its per-book endpoints by design, so
// those queries are disabled for it and its faces render an explicit dormant
// rung; they never read a missing answer as zero.
import { useQuery } from '@tanstack/react-query'
import { accountApi, accountsApi, convictionApi, positionsApi, riskApi, sectorShiftApi } from '../../api/client'
import type { Book, BookRole } from '../../types'
import { isDormant } from './fleetModuleHooks'

export interface BookModuleProps {
  book: Book
}

/**
 * How often the faces that date their figures re-read the clock (`useNow`). It is what
 * makes an age move on an idle tab: when the engine stops writing the confidence log,
 * every refetch returns the same rows, react-query keeps the same `data` reference,
 * and nothing else would ever render the face again.
 */
export const CLOCK_MS = 30_000

/**
 * The book whose conviction data this book shows. A subscriber (validation /
 * live) book has no conviction rows of its own (ruling 26): it mirrors the
 * research book's, so its valve, scorecard and overlay θ read the source.
 */
export const sourceIdOf = (book: Book): number => book.pick_source_account_id ?? book.id

/** `mirrors research` for a subscriber book, whose figures are the source book's; else nothing. */
export const mirrorsCaption = (book: Book): string | undefined =>
  book.pick_source_account_id != null ? 'mirrors research' : undefined

export const ROLE_LABEL: Record<BookRole, string> = {
  research: 'Research',
  validation: 'Validation',
  live: 'Live',
  book: 'Book',
}

/** The three page layouts. The generic `book` role is an unclassified registry row and gets the live layout (ruling 29). */
export function layoutRole(role: BookRole): 'research' | 'validation' | 'live' {
  return role === 'research' || role === 'validation' ? role : 'live'
}

/**
 * The drawer keys of each layout, in on-screen order (spec §5.2). One list per
 * layout, read by BookPage (to build the grid) and by the registry tests.
 */
export const BOOK_MODULE_KEYS: Record<'research' | 'validation' | 'live', string[]> = {
  research: [
    'book-header', 'picks-excess', 'drawdown-floor', 'exposure', 'exit-valve', 'tax',
    'book-value', 'holdings-heat', 'scorecard', 'sector', 'bench', 'cadence', 'overlay',
  ],
  validation: [
    'book-header', 'mirror-fidelity', 'drawdown-floor', 'exposure', 'exit-valve', 'tax',
    'book-value', 'holdings-heat', 'scorecard', 'sector', 'mirror', 'inception',
  ],
  live: [
    'book-header', 'live-gate', 'drawdown-floor', 'exposure', 'exit-valve', 'tax',
    'book-value', 'holdings-heat', 'kill-entries', 'cap-rejects', 'dormancy',
  ],
}

// ------------------------------------------------------------------- queries

/** `['account', id]`: the header's equity / day P&L and the exposure face's cash (same options as BookCard). */
export function useBookAccount(book: Book) {
  return useQuery({
    queryKey: ['account', book.id],
    queryFn: () => accountApi.get(book.id),
    enabled: !isDormant(book),
    retry: false,
    refetchInterval: 10000,
  })
}

/** HoldingsPanel's options: the book's live broker positions. */
export function useBookPositions(book: Book) {
  return useQuery({
    queryKey: ['positions', book.id],
    queryFn: () => positionsApi.getAll(book.id),
    enabled: !isDormant(book),
    retry: 1,
    refetchInterval: 30000,
  })
}

/** RiskBackstopCard's options: this book's drawdown floor (and exposure-scaling target). */
export function useBookDrawdown(book: Book) {
  return useQuery({
    queryKey: ['drawdown-floor', book.id],
    queryFn: () => riskApi.drawdownFloor(book.id),
    enabled: !isDormant(book),
    retry: 1,
    refetchInterval: 60000,
  })
}

/**
 * The conviction reads behind the exit valve, on the SOURCE book (ruling 26).
 * `sourceless` is a dormant book with no pick source (ruling 33): there is no
 * conviction data anywhere for it, and reading its own id would 404 into a false
 * error, so all three reads are off and the face shows a dormant rung. A dormant
 * book WITH a source reads that source's data like any subscriber book.
 */
export function useSourceConviction(book: Book) {
  const id = sourceIdOf(book)
  const sourceless = isDormant(book) && book.pick_source_account_id == null

  const statusQ = useQuery({
    queryKey: ['conviction-status', id],
    queryFn: () => convictionApi.status(id),
    enabled: !sourceless,
    retry: 1,
    refetchInterval: 30000,
  })
  const scorecardQ = useQuery({
    queryKey: ['scorecard', id],
    queryFn: () => convictionApi.scorecard(id),
    enabled: !sourceless,
    retry: 1,
    refetchInterval: 30000,
  })
  const logQ = useQuery({
    queryKey: ['confidence-log', id, 30],
    queryFn: () => convictionApi.confidenceLog(id, 30),
    enabled: !sourceless,
    retry: 1,
    refetchInterval: 60000,
  })

  return { statusQ, scorecardQ, logQ, sourceless }
}

/**
 * The incubation roster behind the bench strip and the cadence timeline
 * (ResearchCadenceStrip's and IncubationRosterPanel's key and options). Both faces
 * mount it at once; react-query shares the one request.
 */
export function useBookRoster(book: Book) {
  return useQuery({
    queryKey: ['conviction-roster', book.id],
    queryFn: () => convictionApi.roster(book.id),
    retry: 1,
    refetchInterval: 30000,
  })
}

/** OverlayCard's and KillEntriesCard's read: the book's own config overlay. */
export function useBookOverlay(book: Book) {
  return useQuery({
    queryKey: ['overlay', book.id],
    queryFn: () => accountsApi.overlay(book.id),
    retry: 1,
    refetchInterval: 60000,
  })
}

/** SectorPanel's read of the evaluator's runs over the last 7 days (the cadence timeline's sector lane). */
export function useSectorRuns(book: Book) {
  return useQuery({
    queryKey: ['sector-shift', 'runs', book.id],
    queryFn: () => sectorShiftApi.runs(book.id, 7),
    retry: 1,
    refetchInterval: 60000,
  })
}
